package com.havn.app.features.tasks.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.supabase.havnSlowSqlDetail
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.domain.HavnTaskDraft
import com.havn.app.features.tasks.domain.HavnTaskPage
import com.havn.app.features.tasks.domain.HavnTaskPriority
import com.havn.app.features.tasks.domain.HavnTaskRepository
import com.havn.app.features.tasks.domain.HavnTaskStatus
import com.havn.app.features.tasks.domain.HavnTaskType
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

class SupabaseHavnTaskRepository(
    private val client: SupabaseClient
) : HavnTaskRepository {
    override suspend fun taskPage(
        agentId: String,
        agencyId: String?,
        statuses: List<HavnTaskStatus>,
        offset: Int,
        limit: Int,
        personalScope: Boolean
    ): Result<HavnTaskPage> {
        val safeOffset = offset.coerceAtLeast(0)
        val safeLimit = limit.coerceAtLeast(1)
        val statusValues = statuses.map { it.databaseValue }.distinct()
        val statusLabel = statusValues.takeIf { it.isNotEmpty() }?.joinToString("|") ?: "all"
        return runCatching {
            val rows = havnSlowSqlDetail("tasks.page(agent_id=$agentId, agency_id=${agencyId ?: "personal"}, personal_scope=$personalScope, status=$statusLabel, offset=$safeOffset, limit=$safeLimit)") {
                client.postgrest.rpc(
                    function = "tasks_page_for_current_user",
                    parameters = TaskPageParams(
                        agencyId = agencyId,
                        statuses = statusValues,
                        offset = safeOffset,
                        limit = safeLimit + 1,
                        personalScope = personalScope
                    )
                ).decodeList<TaskRow>()
            }
            val hasMore = rows.size > safeLimit
            val tasks = rows.take(safeLimit).map { it.toDomain() }
            HavnTaskPage(
                tasks = tasks,
                totalCount = rows.firstOrNull()?.totalCount
                    ?: (safeOffset + tasks.size + if (hasMore) 1 else 0)
            )
        }.onFailure {
            HavnLogger.error("Tasks", "Failed to load HAVN tasks page for $agentId", it)
        }
    }

    override suspend fun createTask(agentId: String, draft: HavnTaskDraft): Result<HavnTask> {
        return runCatching {
            havnSlowSqlDetail("tasks.create(agent_id=$agentId, type=${draft.type.databaseValue})") {
                client.from(TASKS)
                    .insert(draft.toInsertRow(agentId)) {
                        select()
                    }
                    .decodeSingle<TaskRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("Tasks", "Failed to create HAVN task for $agentId", it)
        }
    }

    override suspend fun updateTask(agentId: String, taskId: String, draft: HavnTaskDraft): Result<HavnTask> {
        return runCatching {
            havnSlowSqlDetail("tasks.update(task_id=$taskId, type=${draft.type.databaseValue})") {
                client.from(TASKS)
                    .update(draft.toUpdateRow()) {
                        filter { eq("id", taskId) }
                        select()
                    }
                    .decodeSingle<TaskRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("Tasks", "Failed to update HAVN task $taskId", it)
        }
    }

    override suspend fun updateStatus(
        agentId: String,
        taskId: String,
        status: HavnTaskStatus,
        completedAt: String?
    ): Result<HavnTask> {
        return runCatching {
            havnSlowSqlDetail("tasks.updateStatus(task_id=$taskId, status=${status.databaseValue})") {
                client.from(TASKS)
                    .update(TaskStatusUpdateRow(status = status.databaseValue, completedAt = completedAt)) {
                        filter { eq("id", taskId) }
                        select()
                    }
                    .decodeSingle<TaskRow>()
            }
                .toDomain()
        }.onFailure {
            HavnLogger.error("Tasks", "Failed to update HAVN task $taskId", it)
        }
    }

    private companion object {
        const val TASKS = "tasks"
    }
}

@Serializable
private data class TaskRow(
    val id: String,
    val title: String,
    val description: String? = null,
    val type: String,
    val status: String,
    val priority: String,
    @SerialName("due_at") val dueAt: String? = null,
    @SerialName("completed_at") val completedAt: String? = null,
    @SerialName("assigned_to_user_id") val assignedToUserId: String? = null,
    @SerialName("created_by_user_id") val createdByUserId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("contact_id") val contactId: String? = null,
    @SerialName("open_house_id") val openHouseId: String? = null,
    @SerialName("lead_id") val leadId: String? = null,
    @SerialName("follow_up_id") val followUpId: String? = null,
    @SerialName("ai_generated") val aiGenerated: Boolean = false,
    val notes: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    @SerialName("total_count") val totalCount: Int? = null
) {
    fun toDomain(): HavnTask = HavnTask(
        id = id,
        title = title,
        description = description,
        type = HavnTaskType.fromDatabase(type),
        status = HavnTaskStatus.fromDatabase(status),
        priority = HavnTaskPriority.fromDatabase(priority),
        dueAt = dueAt,
        completedAt = completedAt,
        assignedToUserId = assignedToUserId,
        createdByUserId = createdByUserId,
        agencyId = agencyId,
        propertyId = propertyId,
        contactId = contactId,
        openHouseId = openHouseId,
        leadId = leadId,
        followUpId = followUpId,
        aiGenerated = aiGenerated,
        notes = notes,
        createdAt = createdAt,
        updatedAt = updatedAt
    )
}

@Serializable
private data class TaskInsertRow(
    val title: String,
    val description: String? = null,
    val type: String,
    val status: String,
    val priority: String,
    @SerialName("due_at") val dueAt: String? = null,
    @SerialName("assigned_to_user_id") val assignedToUserId: String? = null,
    @SerialName("created_by_user_id") val createdByUserId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("contact_id") val contactId: String? = null,
    @SerialName("open_house_id") val openHouseId: String? = null,
    @SerialName("lead_id") val leadId: String? = null,
    @SerialName("follow_up_id") val followUpId: String? = null,
    @SerialName("ai_generated") val aiGenerated: Boolean = false,
    val notes: String? = null
)

@Serializable
private data class TaskPageParams(
    @SerialName("p_agency_id") val agencyId: String? = null,
    @SerialName("p_statuses") val statuses: List<String> = emptyList(),
    @SerialName("p_offset") val offset: Int = 0,
    @SerialName("p_limit") val limit: Int = 20,
    @SerialName("p_personal_scope") val personalScope: Boolean = false
)

@Serializable
private data class TaskStatusUpdateRow(
    val status: String,
    @SerialName("completed_at") val completedAt: String? = null
)

@Serializable
private data class TaskUpdateRow(
    val title: String,
    val description: String? = null,
    val type: String,
    val status: String,
    val priority: String,
    @SerialName("due_at") val dueAt: String? = null,
    @SerialName("assigned_to_user_id") val assignedToUserId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("contact_id") val contactId: String? = null,
    @SerialName("open_house_id") val openHouseId: String? = null,
    @SerialName("lead_id") val leadId: String? = null,
    @SerialName("follow_up_id") val followUpId: String? = null,
    val notes: String? = null
)

private fun HavnTaskDraft.toInsertRow(agentId: String): TaskInsertRow = TaskInsertRow(
    title = title.trim(),
    description = description?.trim()?.takeIf { it.isNotBlank() },
    type = type.databaseValue,
    status = status.databaseValue,
    priority = priority.databaseValue,
    dueAt = dueAt,
    assignedToUserId = assignedToUserId,
    createdByUserId = agentId,
    agencyId = agencyId,
    propertyId = propertyId,
    contactId = contactId,
    openHouseId = openHouseId,
    leadId = leadId,
    followUpId = followUpId,
    aiGenerated = aiGenerated,
    notes = notes?.trim()?.takeIf { it.isNotBlank() }
)

private fun HavnTaskDraft.toUpdateRow(): TaskUpdateRow = TaskUpdateRow(
    title = title.trim(),
    description = description?.trim()?.takeIf { it.isNotBlank() },
    type = type.databaseValue,
    status = status.databaseValue,
    priority = priority.databaseValue,
    dueAt = dueAt,
    assignedToUserId = assignedToUserId,
    propertyId = propertyId,
    contactId = contactId,
    openHouseId = openHouseId,
    leadId = leadId,
    followUpId = followUpId,
    notes = notes?.trim()?.takeIf { it.isNotBlank() }
)
